import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InboxSettingsService } from '@app/shared/services/settings/inbox-settings/inbox-settings.service';
import * as fileSaver from 'file-saver';
import { Subscription } from 'rxjs';
import heic2any from 'heic2any';

type FileType = {
  created_at: string;
  file_name: string;
  from_email: string;
  from_name: string;
  id: number;
  is_linked: boolean;
  size: number;
  updated_at: string;
  upload_path: string;
  user_id: number;
  selected?: boolean;
};

type DataType = {
  index: number;
  urlLink: string;
  file: FileType;
};

import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'shared-doc-viewer-dialog',
  templateUrl: './shared-doc-viewer-dialog.component.html',
  styleUrls: ['./shared-doc-viewer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedDocViewerDialogComponent implements OnInit, OnDestroy {
  // define variales
  showNoPreview = false;
  showPdfViewer = false;
  showImageViewer = false;
  isDocumentLoading: boolean = false;

  // define output variables
  @Output() loaderServiceHideEvent = new EventEmitter();
  @Output() cdEvent = new EventEmitter();
  subscription = new Subscription();

  constructor(
    private _dialogRef: MatDialogRef<SharedDocViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public _data: DataType,
    private _inboxService: InboxSettingsService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.showSections();
  }

  ngOnChanges() {
    this.showSections();
  }

  // method to set show sections
  showSections() {
    let fileType: string = this.checkFileType(this._data.file.file_name);
    if (fileType == 'img') {
      this.showImageViewer = true;
      this.showPdfViewer = false;
      this.showNoPreview = false;
    } else if (fileType == 'pdf') {
      this.showImageViewer = false;
      this.showPdfViewer = true;
      this.showNoPreview = false;
    } else if (fileType == 'null') {
      this.showImageViewer = false;
      this.showPdfViewer = false;
      this.showNoPreview = true;
    }
  }

  // method to hide doc viewer
  hideDocViewer() {
    this._dialogRef.close();
  }

  // method to check file type
  checkFileType(fileName: string): string {
    const fileNameFormated = fileName.toLowerCase();
    if (fileNameFormated.includes('heic')) {
      this.convertHEICFile();
      return 'img';
    }
    // check for image
    if (
      fileNameFormated.includes('svg') ||
      fileNameFormated.includes('jpeg') ||
      fileNameFormated.includes('png') ||
      fileNameFormated.includes('jpg') ||
      fileNameFormated.includes('giff')
    ) {
      return 'img';
    }
    // check for pdf
    else if (fileNameFormated.includes('pdf')) {
      return 'pdf';
    }
    // check for nothing
    else {
      return 'null';
    }
  }

  // method to download attachment
  downloadAttachment() {
    this.subscription.add(
      this._inboxService.downloadBlob(this._data?.urlLink).subscribe(
        (result) => {
          fileSaver.saveAs(result, this._data.file.file_name);
        },
        (error: any) => {}
      )
    );
  }

  afterLoadComplete(event: any) {
    this.isDocumentLoading = false;
    this.loaderServiceHideEvent.emit('PDF loaded');
  }

  onError(event: any) {
    this.showPdfViewer = false;
    this.showNoPreview = true;
    this.isDocumentLoading = false;
    this.cdEvent.emit(event);
    this.loaderServiceHideEvent.emit(event);
  }

  onProgress(event) {
    this.isDocumentLoading = true;
    this.loaderServiceHideEvent.emit(event);
  }

  pageRendered(event) {}

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
  async convertHEICFile(): Promise<void> {
    this.isDocumentLoading = true;
    try {
      // Step 1: Download the file blob from S3
      let response = await this._inboxService.downloadBlob(this._data?.urlLink).toPromise();

      // Step 2: If MIME is incorrect (e.g., 'text/xml'), fix it
      if (response.type === 'text/xml') {
        response = new Blob([response], { type: 'image/heic' });
      }
      const convertedBlob = await heic2any({
        blob: response,
        toType: 'image/jpeg',
        quality: 0.5,
      });
      const convertedFile = new File([convertedBlob as Blob], this._data.file.file_name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
      this._data.urlLink = URL.createObjectURL(convertedFile);
      this.cd.detectChanges();
      this.showImageViewer = true;
      this.showPdfViewer = false;
      this.showNoPreview = false;
      this.cd.detectChanges();
    } catch (error) {
      console.error('HEIC conversion failed:', error);
      this.showNoPreview = true;
    } finally {
      this.isDocumentLoading = false;
      this.cd.detectChanges();
    }
  }
}
